import Banner from '@carvana/ds-banner';
import Spinner from '@carvana/ds-spinner';
import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import React from 'react';
import { useBrand } from '|/brand';
import styles from './digital-wallets.module.css';
import { getStripe } from './get-stripe';
import { useDigitalWallets } from './use-digital-wallets';
import { useDigitalWalletsElementsOptions } from './use-digital-wallets-elements-options';
import type { CompletePurchaseSubmitResult } from '../../../use-complete-purchase-vm';
import { DigitalWalletUnavailableBanner } from '../digital-wallet-unavailable-banner';

interface DigitalWalletsProps {
  editPaymentMethod: () => void;
  setIsSubmitting: (value: boolean) => void;
  onSupportChange: (value: boolean) => void;
  onPaymentCollected: () => Promise<CompletePurchaseSubmitResult>;
  onPurchaseRolledBack: () => void;
  onError: () => void;
}

const DigitalWallets = ({
  editPaymentMethod,
  setIsSubmitting,
  onSupportChange,
  onPaymentCollected,
  onPurchaseRolledBack,
  onError
}: DigitalWalletsProps) => {
  const {
    isReady,
    isSupported,
    isSaving,
    error,
    isCardEligible,
    digitalWallet,
    expressCheckoutElementOptions,
    onReady,
    onLoadError,
    onClick,
    onCancel,
    onConfirm
  } = useDigitalWallets({
    setIsSubmitting,
    onSupportChange,
    onPaymentCollected,
    onPurchaseRolledBack,
    onError
  });

  if (!isCardEligible) {
    return (
      <DigitalWalletUnavailableBanner
        reason="CardIneligible"
        digitalWallet={digitalWallet}
        editPaymentMethod={editPaymentMethod}
      />
    );
  }

  if (isReady && !isSupported) {
    return (
      <DigitalWalletUnavailableBanner
        reason="UnsupportedDevice"
        digitalWallet={digitalWallet}
        editPaymentMethod={editPaymentMethod}
      />
    );
  }

  const isLoading = !isReady || isSaving;

  return (
    <>
      {error && (
        <div className={styles.errorBannerContainer}>
          <Banner open type="critical" icon align="left">
            <Banner.Headline>{error.headline}</Banner.Headline>
            <Banner.Message>{error.message}</Banner.Message>
          </Banner>
        </div>
      )}

      {isLoading && (
        <div className={styles.loadingContainer}>
          <Spinner size={36} />
        </div>
      )}

      <div className={classNames(styles.container, isLoading && styles.hidden)}>
        <ExpressCheckoutElement
          options={expressCheckoutElementOptions}
          onReady={onReady}
          onLoadError={onLoadError}
          onClick={onClick}
          onCancel={onCancel}
          onConfirm={onConfirm}
          data-analytics-id="pp_complete_purchase_digital_wallets_element"
        />
      </div>
    </>
  );
};

const DigitalWalletsWrapper = (props: DigitalWalletsProps) => {
  const { brand, identity } = useBrand();
  const stripeKey = brand;
  const stripe = getStripe(identity.stripePublishableKey);
  const stripeElementsOptions = useDigitalWalletsElementsOptions();

  return (
    <Elements key={stripeKey} stripe={stripe} options={stripeElementsOptions}>
      <DigitalWallets {...props} />
    </Elements>
  );
};

export { DigitalWalletsWrapper as DigitalWallets };
